import type { Proposta } from '@/types'
import type { PlanData } from './dealCalculations'
import { formatCurrency } from './format-currency'
import { normalizePropostaForExport, resolvePropostaItems } from './normalizeProposta'
import {
  buildExportPlanDataForItem,
  type BuildExportPlanDataOptions,
} from './proposalExport'
import {
  buildAnnexSummarySectionHtml,
  generateAnnexScopePageHTML,
  generateAnnexSummaryPageHTML,
  type AnnexContractedItem,
} from './pdfAnnexPages'
import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'

export { type AnnexContractedItem } from './pdfAnnexPages'
export { buildAnnexSummarySectionHtml, generateAnnexScopePageHTML, generateAnnexSummaryPageHTML }

export function escapeHtml(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

/** @deprecated Prefer generateAnnexPdf (2 páginas). Mantido para testes legados. */
export function generatePDFHTML(plan: PlanData, empresaNome: string): string {
  const contracted: AnnexContractedItem[] = [{ nome: plan.nome, loyalty: plan.loyaltyDuration }]
  const scope = generateAnnexScopePageHTML(plan, empresaNome, '1/2')
  const summaryInner = buildAnnexSummarySectionHtml(plan, contracted)
  return scope.replace(
    '</div>\n      <div class="footer-logo">',
    `<div class="summary-section" style="margin-top:auto;padding-top:5mm">${summaryInner}</div>\n      <div class="footer-logo">`,
  ).replace('1/2', '1/1')
}

export async function htmlToCanvas(html: string): Promise<HTMLCanvasElement> {
  const scrollY = window.scrollY
  const prevOverflow = document.body.style.overflow
  document.body.style.overflow = 'hidden'

  const host = document.createElement('div')
  host.setAttribute('aria-hidden', 'true')
  host.style.cssText =
    'position:fixed;left:0;top:0;width:794px;transform:translateX(-120vw);overflow:visible;pointer-events:none;z-index:-1;'

  const parsed = new DOMParser().parseFromString(html, 'text/html')
  const styleNode = parsed.querySelector('style')
  const pageNode = parsed.querySelector('.page')
  if (styleNode) host.appendChild(styleNode.cloneNode(true))
  if (pageNode) {
    host.appendChild(pageNode.cloneNode(true))
  } else {
    host.innerHTML = html
  }
  document.body.appendChild(host)

  const target = (host.querySelector('.page') ?? host) as HTMLElement
  target.style.width = '794px'
  target.style.minHeight = '1123px'
  target.style.background = '#ffffff'

  try {
    await new Promise<void>((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
    })

    return await html2canvas(target, {
      scale: 2,
      backgroundColor: '#ffffff',
      logging: false,
      width: 794,
      height: target.scrollHeight || 1123,
      windowWidth: 794,
      useCORS: true,
      onclone: (clonedDoc) => {
        const clonedPage = clonedDoc.querySelector('.page') as HTMLElement | null
        if (!clonedPage) return
        clonedPage.style.visibility = 'visible'
        clonedPage.style.opacity = '1'
        clonedPage.style.width = '794px'
        clonedPage.style.minHeight = '1123px'
        clonedPage.style.background = '#ffffff'
      },
    })
  } finally {
    host.remove()
    document.body.style.overflow = prevOverflow
    window.scrollTo(0, scrollY)
  }
}

export function canvasToPdfPage(pdf: jsPDF, canvas: HTMLCanvasElement, addNewPage: boolean) {
  if (canvas.width <= 0 || canvas.height <= 0) {
    throw new Error('Falha ao renderizar página do Anexo 1')
  }

  const pageWidth = pdf.internal.pageSize.getWidth()
  const pageHeight = pdf.internal.pageSize.getHeight()
  const imgData = canvas.toDataURL('image/png')
  let imgWidth = pageWidth
  let imgHeight = (canvas.height * imgWidth) / canvas.width
  let x = 0
  let y = 0

  if (imgHeight > pageHeight) {
    const scale = pageHeight / imgHeight
    imgWidth *= scale
    imgHeight = pageHeight
    x = (pageWidth - imgWidth) / 2
  }

  if (addNewPage) pdf.addPage()
  pdf.addImage(imgData, 'PNG', x, y, imgWidth, imgHeight)
}

export async function generateAnnexPdf(
  plan: PlanData,
  empresaNome: string,
  contractedItems: AnnexContractedItem[],
): Promise<Blob> {
  const scopeHtml = generateAnnexScopePageHTML(plan, empresaNome, '1/2')
  const summaryHtml = generateAnnexSummaryPageHTML(plan, contractedItems, '2/2')

  const scopeCanvas = await htmlToCanvas(scopeHtml)
  const summaryCanvas = await htmlToCanvas(summaryHtml)

  const pdf = new jsPDF('p', 'mm', 'a4')
  canvasToPdfPage(pdf, scopeCanvas, false)
  canvasToPdfPage(pdf, summaryCanvas, true)

  return pdf.output('blob')
}

/** Anexo com escopo por item (itens independentes, sem combo). */
export async function generateMultiItemAnnexPdf(
  proposal: Proposta,
  empresaNome: string,
  contractedItems: AnnexContractedItem[],
  options: BuildExportPlanDataOptions = {},
): Promise<Blob> {
  const normalized = normalizePropostaForExport(proposal)
  const items = resolvePropostaItems(normalized)
  if (items.length === 0) throw new Error('Nenhum item na proposta')

  const totalPages = items.length + 1
  const scopePlans = items.map((item) => buildExportPlanDataForItem(normalized, item, options))
  const summaryPlan = scopePlans[0]

  const pdf = new jsPDF('p', 'mm', 'a4')

  for (let i = 0; i < scopePlans.length; i += 1) {
    const scopeHtml = generateAnnexScopePageHTML(
      scopePlans[i],
      empresaNome,
      `${i + 1}/${totalPages}`,
    )
    const scopeCanvas = await htmlToCanvas(scopeHtml)
    canvasToPdfPage(pdf, scopeCanvas, i > 0)
  }

  const summaryHtml = generateAnnexSummaryPageHTML(
    summaryPlan,
    contractedItems,
    `${totalPages}/${totalPages}`,
  )
  const summaryCanvas = await htmlToCanvas(summaryHtml)
  canvasToPdfPage(pdf, summaryCanvas, true)

  return pdf.output('blob')
}

export async function htmlToPdf(html: string): Promise<Blob> {
  const canvas = await htmlToCanvas(html)
  const pdf = new jsPDF('p', 'mm', 'a4')
  canvasToPdfPage(pdf, canvas, false)
  return pdf.output('blob')
}

// ─── MULTI-OPÇÃO: PDF Multi-Página com Capa Comparativa ─────────────────────

import type { ComboVariationWithPricing } from '@/types'

function generateCoverHTML(variations: ComboVariationWithPricing[], empresaNome: string): string {
  const cardsHtml = variations.map((v, i) => {
    const hasDiscount = (v.descontoPercentual && v.descontoPercentual > 0) || v.precoFechado !== undefined
    const discountBadge = v.descontoPercentual && v.descontoPercentual > 0
      ? `<div class="badge">${v.descontoPercentual}% OFF</div>`
      : v.precoFechado !== undefined ? `<div class="badge">PREÇO ESPECIAL</div>` : ''
    const originalPrice = hasDiscount && v.valorOriginal !== v.valorFinal
      ? `<div class="original-price">${formatCurrency(v.valorOriginal)}</div>`
      : ''
    const isCenter = i === Math.floor(variations.length / 2)

    const itemsHtml = v.items.slice(0, 6).map(item =>
      `<div class="card-item">✓ ${escapeHtml(item.descricao || '')}</div>`
    ).join('')
    const moreItems = v.items.length > 6
      ? `<div class="card-item more">+ ${v.items.length - 6} itens adicionais</div>`
      : ''

    return `
      <div class="card ${isCenter ? 'card-featured' : ''}">
        ${isCenter ? '<div class="featured-badge">RECOMENDADO</div>' : ''}
        <div class="card-name">${escapeHtml(v.nome.toUpperCase())}</div>
        ${v.descricao ? `<div class="card-desc">${escapeHtml(v.descricao)}</div>` : ''}
        <div class="card-divider"></div>
        <div class="card-items">${itemsHtml}${moreItems}</div>
        <div class="card-pricing">
          ${originalPrice}
          ${discountBadge}
          <div class="card-price">${formatCurrency(v.valorFinal)}</div>
          <div class="card-month">/mês</div>
        </div>
      </div>
    `
  }).join('')

  return `<!DOCTYPE html>
  <html lang="pt-BR">
  <head>
    <meta charset="UTF-8" />
    <style>
      * { margin: 0; padding: 0; box-sizing: border-box; }
      body { font-family: 'Segoe UI', Arial, sans-serif; background: #0f1e14; color: #fff; padding: 30px; }
      .page { width: 794px; min-height: 1123px; background: #0f1e14; display: flex; flex-direction: column; }
      .header { text-align: center; padding: 20px 0 30px; }
      .header-title { font-size: 28pt; font-weight: 800; color: #fff; }
      .header-title span { color: #40C351; }
      .header-sub { font-size: 11pt; color: #72C063; margin-top: 6px; letter-spacing: 1px; text-transform: uppercase; }
      .cards { display: flex; gap: 12px; justify-content: center; flex: 1; }
      .card { background: #0d1a11; border: 1px solid #2a3d2a; border-radius: 12px; padding: 20px 16px; flex: 1; display: flex; flex-direction: column; position: relative; margin-top: 12px; }
      .card-featured { background: #1a3d22; border-color: #40C351; border-width: 2px; }
      .featured-badge { position: absolute; top: -14px; left: 50%; transform: translateX(-50%); background: #40C351; color: #000; font-size: 8pt; font-weight: 800; padding: 3px 10px; border-radius: 20px; white-space: nowrap; }
      .card-name { font-size: 15pt; font-weight: 800; color: #fff; text-align: center; margin-bottom: 6px; }
      .card-desc { font-size: 8pt; color: #999; text-align: center; line-height: 1.4; margin-bottom: 10px; }
      .card-divider { height: 1px; background: #2a4a2a; margin: 10px 0; }
      .card-items { flex: 1; }
      .card-item { font-size: 8pt; color: #ccc; margin-bottom: 4px; line-height: 1.3; }
      .card-item.more { color: #666; font-style: italic; }
      .card-pricing { text-align: center; margin-top: 12px; }
      .original-price { font-size: 9pt; color: #666; text-decoration: line-through; }
      .badge { display: inline-block; background: #E35933; color: #fff; font-size: 7pt; font-weight: 800; padding: 2px 8px; border-radius: 20px; margin: 4px 0; }
      .card-price { font-size: 20pt; font-weight: 800; color: #40C351; margin-top: 4px; }
      .card-month { font-size: 9pt; color: #888; }
      .footer { text-align: center; padding: 20px 0 0; color: #555; font-size: 8pt; }
    </style>
  </head>
  <body>
    <div class="page">
      <div class="header">
        <div class="header-title">Proposta Comercial<span>.</span></div>
        <div class="header-sub">${escapeHtml(empresaNome)} — Escolha a melhor opção</div>
      </div>
      <div class="cards">${cardsHtml}</div>
      <div class="footer">agradadigital. — Documento Confidencial</div>
    </div>
  </body>
  </html>`
}

export async function generateComboVariationPdf(
  variations: ComboVariationWithPricing[],
  empresaNome: string,
): Promise<Blob> {
  if (!variations || variations.length === 0) throw new Error('Nenhuma variação fornecida')

  const pdf = new jsPDF('p', 'mm', 'a4')
  const totalPdfPages = 1 + variations.length * 2

  const coverCanvas = await htmlToCanvas(generateCoverHTML(variations, empresaNome))
  canvasToPdfPage(pdf, coverCanvas, false)

  for (let i = 0; i < variations.length; i++) {
    const v = variations[i]
    const hasDiscount = (v.descontoPercentual && v.descontoPercentual > 0) || v.precoFechado !== undefined
    const discountLabel = v.precoFechado !== undefined
      ? 'PREÇO ESPECIAL'
      : v.descontoPercentual && v.descontoPercentual > 0 ? `${v.descontoPercentual}% OFF` : null

    const planData: PlanData = {
      id: v.id,
      nome: `${v.nome} — ${empresaNome}`,
      descricao: v.descricao || 'Escopo detalhado dos serviços e investimentos propostos.',
      tipo: 'plano',
      servicos: v.items.map(item => ({
        id: item.id,
        nome: item.descricao || '',
        valor: item.valorUnitario || 0,
        categoria: (item as { categoria?: string }).categoria || 'Serviços',
        recorrente: item.tipo !== 'avulso',
      })),
      pricing: {
        valor: v.valorFinal,
        valorMensal: v.valorMensal,
        valorSetup: v.valorSetup,
        desconto: v.descontoPercentual || 0,
        total: v.valorFinal,
        recorrente: v.valorMensal > 0,
        duracao: 12,
      },
      showBonus: hasDiscount,
      bonusText: discountLabel ? `ECONOMIA DE ${formatCurrency(v.valorOriginal - v.valorFinal)} (${discountLabel})` : undefined,
      paymentDetails: `Mensalidade recorrente via boleto ou PIX.`,
      loyaltyDuration: 'Período mínimo de 12 meses.',
    }

    const contracted = [{ nome: v.nome, loyalty: planData.loyaltyDuration }]
    const scopeHtml = generateAnnexScopePageHTML(planData, empresaNome, `${i * 2 + 2}/${totalPdfPages}`)
    const summaryHtml = generateAnnexSummaryPageHTML(planData, contracted, `${i * 2 + 3}/${totalPdfPages}`)

    canvasToPdfPage(pdf, await htmlToCanvas(scopeHtml), true)
    canvasToPdfPage(pdf, await htmlToCanvas(summaryHtml), true)
  }

  return pdf.output('blob')
}
